import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Phone, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CTASection from '../components/common/CTASection';
import { FAQSchema, BreadcrumbSchema } from '../components/seo/SchemaMarkup';
import Seo from '../components/seo/Seo';
import { faqs, businessInfo } from '../data/mock';
import { seoConfig } from '../data/seoConfig';

const FAQsPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const breadcrumbs = [
    { name: 'Home', url: 'https://basinplumbing.com/' },
    { name: "FAQ's", url: 'https://basinplumbing.com/faqs/' }
  ];

  return (
    <>
      <Seo {...seoConfig.faqs} />
      <FAQSchema />
      <BreadcrumbSchema items={breadcrumbs} />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-[#1a3a5c] to-[#0f2438]">
          <div className="max-w-7xl mx-auto px-4">
            <nav className="text-sm text-blue-200 mb-6">
              <Link to="/" className="hover:text-white">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-white">FAQ's</span>
            </nav>
            
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-[#e85a24] rounded-xl flex items-center justify-center">
                <HelpCircle className="text-white" size={28} />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                Frequently Asked Questions
              </h1>
            </div>
            <p className="text-xl text-blue-100 max-w-2xl">
              Find answers to common questions about our plumbing services. 
              Can't find what you're looking for? Give us a call!
            </p>
          </div>
        </section>

        {/* FAQs Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4">
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div 
                  key={index} 
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-[#1a3a5c]/30 transition-all shadow-sm hover:shadow-md"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-semibold text-[#1a3a5c] pr-4 text-lg">{faq.question}</span>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                      openFaq === index ? 'bg-[#e85a24]' : 'bg-gray-100'
                    }`}>
                      {openFaq === index ? (
                        <ChevronUp className="text-white" size={20} />
                      ) : (
                        <ChevronDown className="text-gray-500" size={20} />
                      )}
                    </div>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${
                    openFaq === index ? 'max-h-96' : 'max-h-0'
                  }`}>
                    <div className="px-6 pb-5 border-t border-gray-100">
                      <p className="text-gray-600 leading-relaxed pt-4">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Still Have Questions */}
            <div className="mt-12 text-center bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="text-2xl font-bold text-[#1a3a5c] mb-4">
                Still Have Questions?
              </h2>
              <p className="text-gray-600 mb-6">
                Our friendly team is here to help. Give us a call or send us a message.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a href={`tel:${businessInfo.phone}`}>
                  <Button className="bg-[#e85a24] hover:bg-[#d14a14] text-white">
                    <Phone className="mr-2" size={18} />
                    {businessInfo.phone}
                  </Button>
                </a>
                <Link to="/contact">
                  <Button variant="outline" className="border-[#1a3a5c] text-[#1a3a5c]">
                    Send a Message
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <CTASection />
      </main>

      <Footer />
    </>
  );
};

export default FAQsPage;
